import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { Route } from "src/shared/constants/route";

import { BUFFER_MINUTES } from "../shared/utils/buffer";

const BufferInformationPage: React.FC = () => {
  return (
    <>
      <Header
        title="Shift buffer policy"
        subtitle="How transition time works for volunteer shifts"
      />
      <PageWrapper>
        <Section>
          <Title>What is a buffer?</Title>
          <Body>
            A {BUFFER_MINUTES}-minute buffer is added after shifts that are not
            back-to-back, so there is sufficient transition time for the shift.
            If your next shift starts exactly when the current one ends, no
            buffer is added.
          </Body>
        </Section>

        <Section>
          <Title>How times are shown</Title>
          <Body>
            The schedule calendar and shift list keep the listed wall-clock
            times (for example, 3:00–4:00 PM). Your personal views such as shift
            details, signup confirmation, your shifts, etc., all show the
            effective end time when a buffer applies (for example, 4:15 PM).
          </Body>
        </Section>

        <Section>
          <Title>Scheduling and conflicts</Title>
          <Body>
            Buffer time does not create a shift conflict. You can still sign up
            for back-to-back shifts (for example, 3:00–4:00 PM and 4:00–5:00 PM)
            because conflict checks use the listed times, not the buffered end.
          </Body>
        </Section>

        <Section>
          <Title>Volunteer hours</Title>
          <Body>
            When a buffer applies, that {BUFFER_MINUTES} minutes counts toward
            your volunteer hours. Hours on your dashboard are calculated from
            your effective shift end times.
          </Body>
        </Section>

        <Section>
          <Title>Shift expectations with buffer time</Title>
          <Body>
            The buffer is part of your shift. You are expected to stay for the
            full {BUFFER_MINUTES}-minute duration when it applies. Depending on
            circumstances day-of, a volunteer lead or organizer may allow you to
            leave earlier.
          </Body>
        </Section>
      </PageWrapper>
    </>
  );
};

const Section = styled.section`
  max-width: 720px;
  margin-bottom: 28px;
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 22px;
  font-weight: 900;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const Body = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

export default BufferInformationPage;

/** Inline “Learn more” link used next to buffer explanations. */
export const BufferLearnMoreLink: React.FC<{ className?: string }> = ({
  className,
}) => (
  <LearnMore
    className={className}
    to={Route.VOLUNTEER_BUFFER_INFORMATION}
    target="_blank"
    rel="noopener noreferrer"
  >
    Learn more
  </LearnMore>
);

const LearnMore = styled(Link)`
  font-weight: 700;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`;
